import { Download01, Play } from "@untitled-ui/icons-react";
import { type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const SPEEDS = [
  { rate: 0.5, label: "Slow" },
  { rate: 1, label: "Normal" },
  { rate: 1.2, label: "Medium" },
  { rate: 1.5, label: "Fast" },
  { rate: 1.7, label: "Very fast" },
  { rate: 2, label: "Super fast" },
] as const;

export function VoicePlayButton({
  isPlaying,
  onClick,
  className,
  label,
  compact = false,
  pending = false,
  download = false,
}: {
  isPlaying: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  label: string;
  compact?: boolean;
  pending?: boolean;
  download?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      disabled={pending}
      className={cn(
        "bg-primary-600 flex shrink-0 cursor-pointer items-center justify-center rounded-full text-white disabled:cursor-wait",
        compact ? "size-6" : "size-9",
        className,
      )}
    >
      {pending ? (
        <span
          className={cn(
            "animate-spin rounded-full border-2 border-current border-t-transparent",
            compact ? "size-3" : "size-4",
          )}
        />
      ) : download ? (
        <Download01 className={compact ? "size-3" : "size-4"} />
      ) : isPlaying ? (
        <span className="flex items-center gap-[3px]">
          <span
            className={cn(
              "rounded-[1.125px] bg-current",
              compact ? "h-2.5 w-0.5" : "h-3.5 w-[3px]",
            )}
          />
          <span
            className={cn(
              "rounded-[1.125px] bg-current",
              compact ? "h-2.5 w-0.5" : "h-3.5 w-[3px]",
            )}
          />
        </span>
      ) : (
        <Play
          className={cn(
            "translate-x-px [&_path]:fill-current",
            compact ? "size-3" : "size-4",
          )}
        />
      )}
    </button>
  );
}

export function VoiceSpeedButton({
  rate,
  onChange,
  disabled,
}: {
  rate: number;
  onChange: (rate: number) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("messenger.user");
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={t("Playback speed")}
          className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="border-primary-600 text-primary-600 en-number flex h-[18px] min-w-5 items-center justify-center rounded border px-1 text-[10px] leading-none font-semibold">
            {rate}X
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-max min-w-32 p-0 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        {SPEEDS.map((item) => {
          const selected = item.rate === rate;

          return (
            <button
              key={item.rate}
              type="button"
              onClick={() => {
                onChange(item.rate);
                setOpen(false);
              }}
              className="flex h-[42px] w-full items-center px-1.5"
            >
              <span
                className={cn(
                  "flex h-[38px] w-full items-center gap-2 rounded-md px-2.5 text-sm font-medium",
                  selected ? "text-primary-600" : "text-gray-700",
                )}
              >
                <span className="en-number shrink-0">{item.rate}X</span>
                <span>{t(item.label)}</span>
              </span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
